import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Plus, Flame } from "lucide-react";

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.08 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

function HeatLevel({ level }) {
  return (
    <div className="flex items-center gap-1" data-testid="heat-level">
      {[1, 2, 3, 4, 5].map((i) => (
        <Flame
          key={i}
          size={14}
          className={i <= level ? "text-[#E07A5F]" : "text-white/15"}
          fill={i <= level ? "#E07A5F" : "none"}
        />
      ))}
    </div>
  );
}

function BadgeTag({ badge }) {
  if (!badge) return null;
  const cls =
    badge === "Bestseller"
      ? "badge-bestseller"
      : badge === "Nuevo"
      ? "badge-new"
      : badge === "Mini"
      ? "badge-mini"
      : badge === "Próximamente"
      ? "badge-limited"
      : "badge-limited";
  return (
    <span
      className={`absolute top-4 left-4 z-20 px-3 py-1 text-[10px] uppercase tracking-widest font-bold ${cls}`}
      data-testid={`badge-${badge.toLowerCase()}`}
    >
      {badge}
    </span>
  );
}

export default function ProductShowcase({ products, onAddToCart }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.1 });

  return (
    <section
      id="products"
      data-testid="products-section"
      className="relative py-24 md:py-32"
      style={{ backgroundColor: "#1A1A1A" }}
    >
      {/* Section header */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.4 }}
        >
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
            La Colección
          </p>
          <h2
            className="text-4xl md:text-5xl font-bold text-[#F5F5F0] tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Creada para el paladar<br />
            <span className="text-[#E07A5F]">que exige lo mejor</span>
          </h2>
        </motion.div>
      </div>

      {/* Talavera divider */}
      <div className="talavera-divider max-w-7xl mx-auto mb-16" />

      {/* Product grid */}
      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
      >
        {products.map((product) => (
          <motion.div
            key={product.id}
            variants={cardVariants}
            className="product-card group relative bg-[#262626] border border-white/5 hover:border-[#D4AF37]/40 transition-all duration-500 overflow-hidden"
            data-testid={`product-card-${product.id}`}
          >
            <BadgeTag badge={product.badge} />

            {/* Image */}
            <div className="aspect-[4/5] overflow-hidden bg-[#151515] relative">
              <img
                src={product.image}
                alt={product.name}
                className="product-image-zoom w-full h-full object-cover"
                loading="lazy"
                data-testid={`product-image-${product.id}`}
              />
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-[#1A1A1A]/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                <button
                  data-testid={`add-to-cart-${product.id}`}
                  onClick={() => onAddToCart(product)}
                  className="bg-[#D4AF37] text-[#1A1A1A] p-4 opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-400 hover:bg-[#E5C158] gold-glow"
                >
                  <Plus size={20} strokeWidth={2} />
                </button>
              </div>
            </div>

            {/* Info */}
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <HeatLevel level={product.heat_level} />
                <span className="text-xs text-[#A3A3A3] uppercase tracking-wider">
                  {product.weight}
                </span>
              </div>
              <h3
                className="text-lg font-semibold text-[#F5F5F0] mb-1"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {product.name}
              </h3>
              <p className="text-xs text-[#A3A3A3] italic mb-3">{product.subtitle}</p>
              <p className="text-xs text-[#A3A3A3] leading-relaxed mb-4">
                {product.description}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold text-[#D4AF37]">
                  ${product.price.toFixed(0)} <span className="text-xs font-normal text-[#A3A3A3]">MXN</span>
                </span>
                <button
                  data-testid={`add-btn-${product.id}`}
                  onClick={() => onAddToCart(product)}
                  className="text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-[#D4AF37] transition-colors border-b border-transparent hover:border-[#D4AF37] pb-0.5"
                >
                  Agregar al carrito
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}
